import { useEffect } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

const FAVICON_TESTID = "dynamic-favicon";
const APPLE_TESTID = "dynamic-apple-touch-icon";

function absolutise(url) {
  if (!url) return null;
  return url.startsWith("http") ? url : `${BACKEND}${url}`;
}

function ensureLink({ rel, href, sizes, type, testId }) {
  if (!href) return;
  // Remove any prior dynamic icon link we may have added.
  document.querySelectorAll(`link[data-testid="${testId}"]`).forEach((n) => n.remove());
  // Also remove the static default favicon CRA injects (if any) so we don't dual-render.
  if (rel === "icon") {
    document.querySelectorAll('link[rel="icon"]:not([data-testid])').forEach((n) => n.remove());
    document.querySelectorAll('link[rel="shortcut icon"]:not([data-testid])').forEach((n) => n.remove());
  }
  const link = document.createElement("link");
  link.rel = rel;
  link.href = href + (href.includes("?") ? "&" : "?") + "v=" + Date.now(); // cache-bust on update
  if (sizes) link.sizes = sizes;
  if (type) link.type = type;
  link.setAttribute("data-testid", testId);
  document.head.appendChild(link);
}

/**
 * Pulls /api/public/settings once on mount and injects:
 *  - <link rel="icon"> from settings.favicon_url
 *  - <link rel="apple-touch-icon"> from settings.apple_touch_icon_url
 *  - <title> from settings.site_title
 *
 * Falls back to logo_url when favicon_url is empty. Silent if the call fails
 * (the page-level CRA defaults stay in place).
 */
export default function useBrandIdentity() {
  useEffect(() => {
    let mounted = true;
    axios.get(`${API}/public/settings`).then((r) => {
      if (!mounted) return;
      const s = r.data || {};
      // 1. Title is now per-route via usePageMeta; useBrandIdentity only
      // manages the favicon/apple-touch icons and theme colour so we don't
      // overwrite the per-page title that pages set after mount.
      // 2. Favicon
      const favHref = absolutise(s.favicon_url || s.logo_url);
      if (favHref) {
        // Guess type from extension; fall back to image/png for safety.
        const ext = (favHref.split(".").pop() || "").toLowerCase().split("?")[0];
        const typeMap = { svg: "image/svg+xml", ico: "image/x-icon", png: "image/png", jpg: "image/jpeg", jpeg: "image/jpeg", webp: "image/webp" };
        ensureLink({ rel: "icon", href: favHref, type: typeMap[ext] || "image/png", testId: FAVICON_TESTID });
      }
      // 3. Apple touch icon
      const appleHref = absolutise(s.apple_touch_icon_url || s.favicon_url || s.logo_url);
      if (appleHref) {
        ensureLink({ rel: "apple-touch-icon", href: appleHref, sizes: "180x180", testId: APPLE_TESTID });
      }
      // 4. Theme colour (saffron house brand)
      let theme = document.querySelector('meta[name="theme-color"]');
      if (!theme) {
        theme = document.createElement("meta");
        theme.setAttribute("name", "theme-color");
        document.head.appendChild(theme);
      }
      theme.setAttribute("content", "#C2410C");
    }).catch(() => {
      // Soft fail — keep whatever index.html ships.
    });
    return () => { mounted = false; };
  }, []);
}
